import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import axios from "axios";
import { toast } from "react-toastify";

interface ChemoDocument {
  name: string;
  url: string;
  filename?: string;
  note?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ChemotherapyProps {
  onChange: (data: any) => void;
  data: any;
  otherTabsData: any;
  profile: any;
}

const panelSx = {
  bgcolor: "#F3F4F6",
  border: "1px solid #E5E7EB",
  borderRadius: "12px",
  p: 2,
};

const headerCellSx = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#fff",
  whiteSpace: "nowrap",
  py: 1,
};

const bodyCellSx = {
  fontSize: "12px",
  py: 0.8,
};

const Chemotherapy: React.FC<ChemotherapyProps> = ({ onChange, data, otherTabsData, profile }) => {
  const actorName = profile?.data?.fullName || profile?.data?.firstName || "";
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<ChemoDocument[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadNote, setUploadNote] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [updateIndex, setUpdateIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [updateNote, setUpdateNote] = useState("");
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!Array.isArray(data?.documents)) return;
    setDocuments(byNewest(data.documents));
  }, [data]);

  useEffect(() => {
    onChange?.({ documents });
  }, [documents]);

  const now = () => dayjs().format("DD.MM.YYYY hh:mm A");

  const byNewest = (list: ChemoDocument[]) =>
    [...list].sort((a, b) => {
      const left = dayjs(a.uploadedAt, "DD.MM.YYYY hh:mm A");
      const right = dayjs(b.uploadedAt, "DD.MM.YYYY hh:mm A");
      return (right.isValid() ? right.valueOf() : 0) - (left.isValid() ? left.valueOf() : 0);
    });

  const persist = async (nextDocuments: ChemoDocument[]) => {
    if (!otherTabsData?._id) return;
    await axios.post(
      `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/CreateFormData`,
      {
        formName: "Chemotherapy",
        formData: { documents: nextDocuments },
        searchId: otherTabsData._id,
        patientId: otherTabsData?.patientData?._id,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  const uploadOne = async (file: File): Promise<Pick<ChemoDocument, "name" | "url" | "filename">> => {
    const body = new FormData();
    body.append("file", file);
    const response = await axios.post(`${process.env.REACT_APP_ADMIN_API_URL}/file/upload`, body, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    const fileUrl = response?.data?.data?.fileUrl;
    if (!response?.data?.isSuccess || !fileUrl) {
      throw new Error(response?.data?.message || `Could not upload ${file.name}`);
    }
    return {
      name: file.name,
      url: fileUrl,
      filename: response?.data?.data?.filename,
    };
  };

  const deleteStoredFile = async (document: ChemoDocument) => {
    if (!document?.url && !document?.filename) return;
    const response = await axios.delete(`${process.env.REACT_APP_ADMIN_API_URL}/file/delete`, {
      params: { fileUrl: document.url || "", filename: document.filename || "" },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (response?.data?.isSuccess === false) {
      throw new Error(response?.data?.message || "Could not delete the file from storage");
    }
  };

  const closeUpload = () => {
    if (uploading) return;
    setUploadOpen(false);
    setUploadFile(null);
    setUploadNote("");
  };

  const confirmUpload = async () => {
    if (!uploadFile) {
      toast.error("Choose one document to upload");
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadOne(uploadFile);
      const stamp = now();
      const nextDocuments = byNewest([
        ...documents,
        {
          ...uploaded,
          note: uploadNote.trim(),
          uploadedBy: actorName,
          uploadedAt: stamp,
          updatedBy: "",
          updatedAt: "",
        },
      ]);
      setDocuments(nextDocuments);
      await persist(nextDocuments);
      toast.success("Document saved with this admission");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadNote("");
    } catch (error: any) {
      toast.error(error?.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async () => {
    if (deleteIndex == null) return;
    const current = documents[deleteIndex];
    setDeleting(true);
    try {
      await deleteStoredFile(current);
      const nextDocuments = documents.filter((_, itemIndex) => itemIndex !== deleteIndex);
      setDocuments(nextDocuments);
      await persist(nextDocuments);
      toast.success("Document deleted");
      setDeleteIndex(null);
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  const openUpdate = (index: number) => {
    setUpdateIndex(index);
    setUpdateNote(documents[index]?.note || "");
    setUpdateFile(null);
  };

  const confirmUpdate = async () => {
    if (updateIndex == null) return;
    const current = documents[updateIndex];
    setUpdating(true);
    try {
      const stamp = now();
      let nextDocument: ChemoDocument = {
        ...current,
        note: updateNote.trim(),
        updatedBy: actorName,
        updatedAt: stamp,
      };
      if (updateFile) {
        const uploaded = await uploadOne(updateFile);
        await deleteStoredFile(current);
        nextDocument = {
          ...current,
          ...uploaded,
          note: updateNote.trim(),
          uploadedBy: current.uploadedBy || "",
          uploadedAt: current.uploadedAt || "",
          updatedBy: actorName,
          updatedAt: stamp,
        };
      }
      const nextDocuments = documents.map((item, index) => (index === updateIndex ? nextDocument : item));
      setDocuments(nextDocuments);
      await persist(nextDocuments);
      toast.success("Document updated");
      setUpdateIndex(null);
      setUpdateFile(null);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update document");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
      <Box sx={panelSx}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>Admission Documents</Typography>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              Upload one report or file at a time for this chemotherapy admission. <span style={{ fontWeight: 600 }}>Only PDF files can be viewed directly. All other document formats will be downloaded.</span>
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              setUploadFile(null);
              setUploadNote("");
              setUploadOpen(true);
            }}
          >
            Upload
          </Button>
        </Box>

        <TableContainer sx={{ mt: 1.5, bgcolor: "#fff", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#1565c0" }}>
                <TableCell sx={headerCellSx}>SN</TableCell>
                <TableCell sx={headerCellSx}>Document</TableCell>
                <TableCell sx={headerCellSx}>Note</TableCell>
                <TableCell sx={headerCellSx}>Uploaded By</TableCell>
                <TableCell sx={headerCellSx}>Uploaded At</TableCell>
                <TableCell sx={headerCellSx}>Updated By</TableCell>
                <TableCell sx={headerCellSx}>Updated At</TableCell>
                <TableCell sx={headerCellSx} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ ...bodyCellSx, color: "#777", textAlign: "center" }}>
                    No documents uploaded yet.
                  </TableCell>
                </TableRow>
              )}
              {documents.map((document, index) => (
                <TableRow key={`${document.url}-${index}`} hover>
                  <TableCell sx={bodyCellSx}>{index + 1}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600, maxWidth: 180 }}>{document.name}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, maxWidth: 160 }}>{document.note || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{document.uploadedBy || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{document.uploadedAt || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{document.updatedBy || "—"}</TableCell>
                  <TableCell sx={bodyCellSx}>{document.updatedAt || "—"}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Tooltip title="View / Download" placement="top">
                      <IconButton size="small" aria-label="Open document" onClick={() => window.open(document.url, "_blank")}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Update" placement="top">
                      <IconButton size="small" aria-label="Update document" onClick={() => openUpdate(index)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" placement="top">
                      <IconButton size="small" aria-label="Delete document" onClick={() => setDeleteIndex(index)}>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={uploadOpen} onClose={closeUpload} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, position: "relative", pr: 6 }}>
          Upload document
          <IconButton aria-label="close" onClick={closeUpload} disabled={uploading} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Comment / Note"
            value={uploadNote}
            onChange={(event) => setUploadNote(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <Button component="label" variant="outlined" size="small">
            {uploadFile ? uploadFile.name : "Choose file"}
            <input
              hidden
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
            />
          </Button>
          <Typography sx={{ fontSize: "12px", color: "#666", mt: 1 }}>
            Only one document can be uploaded at a time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeUpload} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmUpload} disabled={uploading || !uploadFile}>
            {uploading ? <CircularProgress size={18} color="inherit" /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteIndex != null} onClose={() => !deleting && setDeleteIndex(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete document</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px" }}>
            Delete {deleteIndex != null ? documents[deleteIndex]?.name : "this document"}? This also removes the file from storage.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteIndex(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={removeDocument} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateIndex != null} onClose={() => !updating && setUpdateIndex(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, position: "relative", pr: 6 }}>
          Update document
          <IconButton
            aria-label="close"
            onClick={() => setUpdateIndex(null)}
            disabled={updating}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "13px", mb: 1.5, mt: 0.5 }}>
            {updateIndex != null ? documents[updateIndex]?.name : ""}
          </Typography>
          <TextField
            label="Comment / Note"
            value={updateNote}
            onChange={(event) => setUpdateNote(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            sx={{ mb: 2 }}
          />
          <Button component="label" variant="outlined" size="small">
            {updateFile ? updateFile.name : "Choose new file"}
            <input
              hidden
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(event) => setUpdateFile(event.target.files?.[0] || null)}
            />
          </Button>
          <Typography sx={{ fontSize: "12px", color: "#666", mt: 1 }}>
            Choosing a new file replaces the current file and deletes the old one from storage.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setUpdateIndex(null)} disabled={updating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmUpdate} disabled={updating}>
            {updating ? <CircularProgress size={18} color="inherit" /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chemotherapy;
